"use client";

import { useMutation } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { Save } from "lucide-react";
import { ErrorBanner } from "@qrody/ui";
import {
  createCode,
  type CreateCodeDesign,
  type CreateCodeOptions,
  type CreateCodePayload,
  type QrCodeResponse,
} from "@qrody/api-client/codes";
import type { TranslatedError } from "@qrody/api-client/problem";

export interface SaveActionProps {
  /** Validated payload — caller guarantees buildPayload returned non-null. */
  payload: CreateCodePayload;
  design: CreateCodeDesign;
  options?: CreateCodeOptions;
  /** Caller may disable the button while the form is in an invalid state. */
  disabled?: boolean;
}

/**
 * Phase 2 of ADR-023: the save path. Posts the captured form-state to
 * `POST /v1/codes`, then routes to `/{locale}/codes/{id}` so the user
 * lands on the new code's detail page (mirrors the create-code-form
 * behaviour we are replacing, but on the per-code surface instead of
 * the list).
 *
 * Stays a small, self-contained piece — the QR generator wires it in
 * place of the download button when `caps.primaryAction === "save"`.
 */
export function SaveAction({
  payload,
  design,
  options,
  disabled,
}: SaveActionProps) {
  const t = useTranslations("generator.action");
  const router = useRouter();
  const locale = useLocale();

  const mutation = useMutation<QrCodeResponse, TranslatedError>({
    mutationFn: async () => {
      const result = await createCode({ payload, design, options });
      if (!result.ok) throw result.error;
      return result.value;
    },
    onSuccess: (code) => {
      router.push(`/${locale}/codes/${code.id}` as never);
    },
  });

  const buttonDisabled = disabled || mutation.isPending;

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => mutation.mutate()}
        disabled={buttonDisabled}
        aria-label={t("save")}
        className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Save className="h-3.5 w-3.5" aria-hidden />
        {mutation.isPending ? t("saving") : t("save")}
      </button>
      {mutation.error ? <ErrorBanner error={mutation.error} /> : null}
    </div>
  );
}
